import React, { useState, useEffect } from "react";
import ReactSelect from "react-select";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { format } from "date-fns";
import { DEBRIEF_PDF_REPORT_STATUS, REPORT_TYPE_CHOICES } from "data/AdminData";
import {
  UPDATE_DEBRIEF_PDF_REPORT_PAGE_NUMBER,
  SG_GET_DEBRIEF_PDF_REPORT_TEMPLATES,
  SG_GET_DEBRIEF_PDF_REPORT,
  SG_GET_ORGANIZATION_SELECT,
  RESET_DEBRIEF_PDF_REPORT_UPDATES,
  UPDATE_DEBRIEF_PDF_REPORT_QUERY_PARAMS,
} from "constants/actions";

// shadcn/ui components
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Separator } from "components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { 
  Plus, 
  Search, 
  Filter, 
  X, 
  Globe, 
  Building2, 
  FileText, 
  Copy, 
  Eye, 
  Edit, 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  SlidersHorizontal
} from "lucide-react";


const TemplateList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  let ReloadPage = searchParams.get("reload");

  // const [organizationSelect, setOrganizationSelect] = useState([])
  const [orgSelected, setOrgSelected] = useState([]);
  const [resportStatus, setReportStatus] = useState([]);
  const [reportType, setReportType] = useState([]);
  const [globalTemplate, setGlobalTemplate] = useState(false);
  const [orgTemplate, setOrgTemplate] = useState(false);
  const [globalTemplateName, setGlobalTemplateName] = useState("");
  const [orgTemplateName, setOrgTemplateName] = useState("");
  const [reportName, setReportName] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [queryParams, setQueryParams] = useState([]);

  const { get_debrief_pdf_report, get_organization_select } = useSelector(
    (state) => ({
      get_debrief_pdf_report: state.debrief_pdf_report,
      get_organization_select: state.organizations.organization_select,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (queryParams?.length > 0) {
      dispatch({
        type: SG_GET_DEBRIEF_PDF_REPORT,
        payload: queryParams.join("&"),
      });
    } else {
      dispatch({
        type: SG_GET_DEBRIEF_PDF_REPORT,
      });
    }

    dispatch({
      type: SG_GET_ORGANIZATION_SELECT,
    });
    dispatch({
      type: SG_GET_DEBRIEF_PDF_REPORT_TEMPLATES,
    });
  }, [dispatch, queryParams]);

  useEffect(() => {
    if (get_debrief_pdf_report?.pending) {
      setLoading(get_debrief_pdf_report?.pending);
    }
    const timer1 = setTimeout(() => {
      setLoading(get_debrief_pdf_report?.pending);
    }, 500);
    return () => clearTimeout(timer1);
  }, [get_debrief_pdf_report]);

  useEffect(() => {
    if (get_debrief_pdf_report?.queryParams) {
      setQueryParams(get_debrief_pdf_report?.queryParams);
    }
  }, [get_debrief_pdf_report]);

  useEffect(() => {
    if (queryParams?.length > 0) {
      const parameters = {};

      queryParams.forEach((item) => {
        const [name, value] = item.split("=");
        parameters[name] = value;
      });
      if (parameters?.["status"]) {
        setReportStatus(
          DEBRIEF_PDF_REPORT_STATUS.filter(
            (status) => status.id == parameters["status"]
          )[0]
        );
      }

      // Check if organization filter is present
      if (parameters?.["organization"]) {
        setOrgSelected(
          get_organization_select.filter(
            (org) => org.id == parameters["organization"]
          )[0]
        );
      }
    }
  }, [queryParams, get_organization_select]);

  function ResetUpdates() {
    dispatch({
      type: RESET_DEBRIEF_PDF_REPORT_UPDATES,
    });
  }

  function applyFilters(statusId, orgId, pageNumber = 1) {
    let _queryParams = [];
    if (pageNumber) {
      _queryParams.push(`page=${pageNumber}`);
    }
    if (statusId) {
      _queryParams.push(`status=${statusId}`);
    }
    if (orgId) {
      _queryParams.push(`organization=${orgId}`);
    }
    if (reportType?.id) {
      _queryParams.push(`report_type=${reportType.id}`);
    }
    if (globalTemplate) {
      _queryParams.push(`global_template=${globalTemplate ? "true" : "false"}`);
    }
    if (orgTemplate) {
      _queryParams.push(`org_template=${orgTemplate ? "true" : "false"}`);
    }
    if (globalTemplateName) {
      _queryParams.push(`global_template_name=${encodeURIComponent(globalTemplateName)}`);
    }
    if (orgTemplateName) {
      _queryParams.push(`org_template_name=${encodeURIComponent(orgTemplateName)}`);
    }
    if (reportName) {
      _queryParams.push(`report_name=${encodeURIComponent(reportName)}`);
    }
    dispatch({
      type: UPDATE_DEBRIEF_PDF_REPORT_QUERY_PARAMS,
      payload: _queryParams,
    });
    dispatch({
      type: SG_GET_DEBRIEF_PDF_REPORT,
      payload: _queryParams.join("&"),
    });
  }

  function handleStatusFilter() {
    applyFilters(resportStatus?.id, orgSelected?.id);
  }

  function UpdateOrganization() {
    applyFilters(resportStatus?.id, orgSelected?.id);
  }

  useEffect(() => {
    setTemplates(get_debrief_pdf_report?.templates?.response);
    setResults(get_debrief_pdf_report?.debrief_pdf_report?.results);
    setPage(get_debrief_pdf_report?.pageNumber);
    setTotalPages(
      Math.ceil(
        get_debrief_pdf_report?.debrief_pdf_report?.count /
          get_debrief_pdf_report?.debrief_pdf_report?.page_size
      )
    );
    if (
      get_debrief_pdf_report?.debrief_pdf_report?.count %
      get_debrief_pdf_report?.debrief_pdf_report?.page_size
    ) {
      setTotalPages(
        Math.ceil(
          get_debrief_pdf_report?.debrief_pdf_report?.count /
            get_debrief_pdf_report?.debrief_pdf_report?.page_size
        )
      );
    }
  }, [get_debrief_pdf_report, page, ReloadPage]);

  function handlePageChange(event, value) {
    setPage(value.activePage);

    dispatch({
      type: UPDATE_DEBRIEF_PDF_REPORT_PAGE_NUMBER,
      payload: value.activePage,
    });
    applyFilters(resportStatus?.id, orgSelected?.id, value.activePage);
  }

  const [templateView, setTemplateView] = useState('all'); // 'all', 'global', 'org'
  const [showFilters, setShowFilters] = useState(false);

  const handleTemplateViewChange = (view) => {
    setTemplateView(view);
    setGlobalTemplate(view === 'global');
    setOrgTemplate(view === 'org');
    setGlobalTemplateName("");
    setOrgTemplateName("");
    setReportName("");
    setPage(1);

    let _queryParams = [`page=1`];
    if (resportStatus?.id) _queryParams.push(`status=${resportStatus.id}`);
    if (orgSelected?.id) _queryParams.push(`organization=${orgSelected.id}`);
    if (reportType?.id) _queryParams.push(`report_type=${reportType.id}`);
    
    if (view === 'global') _queryParams.push(`global_template=true`);
    if (view === 'org') _queryParams.push(`org_template=true`);

    dispatch({ type: UPDATE_DEBRIEF_PDF_REPORT_QUERY_PARAMS, payload: _queryParams });
    dispatch({ type: SG_GET_DEBRIEF_PDF_REPORT, payload: _queryParams.join("&") });
  };

  const clearFilters = () => {
    setOrgSelected([]);
    setReportStatus([]);
    setReportType([]);
    setGlobalTemplateName("");
    setOrgTemplateName("");
    setReportName("");
    setPage(1);
    setTemplateView('all');
    dispatch({ type: UPDATE_DEBRIEF_PDF_REPORT_QUERY_PARAMS, payload: ['page=1'] });
    dispatch({ type: SG_GET_DEBRIEF_PDF_REPORT, payload: 'page=1' });
  };

  const activeFiltersCount = [
    orgSelected?.id,
    resportStatus?.id,
    reportType?.id,
    globalTemplateName,
    orgTemplateName,
    reportName,
  ].filter(Boolean).length;

  console.log("TemplateList results:", results);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">PDF Reports</h1>
            <p className="text-sm text-slate-600 mt-1">
              Manage and configure debrief PDF reports
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate('/app/pdf-reports/create')} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Report
            </Button>
            <Button variant="outline" onClick={() => navigate('/app/pdf-reports/scanner')} className="gap-2">
              <Search className="h-4 w-4" />
              Scanner
            </Button>
            <Button variant="outline" onClick={() => navigate('/app/pdf-reports/templates/')} className="gap-2">
              <FileText className="h-4 w-4" />
              Templates
            </Button>
            <Button variant="outline" onClick={() => navigate('/app/pdf-reports/copy-global-to-org')} className="gap-2">
              <Copy className="h-4 w-4" />
              Copy Template
            </Button>
          </div>
        </div>

        {/* Template Type Selector & Filters */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <Tabs value={templateView} onValueChange={handleTemplateViewChange} className="w-auto">
                  <TabsList>
                    <TabsTrigger value="all" className="gap-2">
                      <FileText className="h-4 w-4" />
                      All Reports
                    </TabsTrigger>
                    <TabsTrigger value="global" className="gap-2">
                      <Globe className="h-4 w-4" />
                      Global Templates
                    </TabsTrigger>
                    <TabsTrigger value="org" className="gap-2">
                      <Building2 className="h-4 w-4" />
                      Org Templates
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    <Filter className="h-3 w-3" />
                    {activeFiltersCount} active
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                {activeFiltersCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-2">
                    <X className="h-4 w-4" />
                    Clear All
                  </Button>
                )}
                <Button 
                  variant={showFilters ? "default" : "outline"} 
                  size="sm" 
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-2"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  {showFilters ? 'Hide' : 'Show'} Filters
                </Button>
              </div>
            </div>
          </CardHeader>

          {showFilters && (
            <CardContent className="pt-0">
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div className="space-y-2">
                    <Label>Organization</Label>
                    <ReactSelect
                      options={get_organization_select}
                      isClearable
                      onChange={setOrgSelected}
                      getOptionLabel={(option) => `#${option.id} - ${option.name}`}
                      getOptionValue={(option) => `${option.id}`}
                      value={orgSelected}
                      placeholder="Select organization..."
                      styles={{
                        control: (base) => ({ ...base, minHeight: 40 }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <ReactSelect
                      options={DEBRIEF_PDF_REPORT_STATUS}
                      isClearable
                      onChange={setReportStatus}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => `${option.id}`}
                      value={resportStatus}
                      placeholder="Select status..."
                      styles={{
                        control: (base) => ({ ...base, minHeight: 40 }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Report Type</Label>
                    <ReactSelect
                      options={REPORT_TYPE_CHOICES}
                      isClearable
                      onChange={setReportType}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => `${option.id}`}
                      value={reportType}
                      placeholder="Select type..."
                      styles={{
                        control: (base) => ({ ...base, minHeight: 40 }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Report Name</Label>
                    <Input
                      placeholder="Search by name..."
                      value={reportName}
                      onChange={(e) => setReportName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Global Template Name</Label>
                    <Input
                      placeholder="Search global template..."
                      value={globalTemplateName}
                      onChange={(e) => setGlobalTemplateName(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Org Template Name</Label>
                    <Input
                      placeholder="Search org template..."
                      value={orgTemplateName}
                      onChange={(e) => setOrgTemplateName(e.target.value)}
                    />
                  </div>
                </div>

                <Separator />

                <div className="flex justify-end">
                  <Button onClick={() => applyFilters(resportStatus?.id, orgSelected?.id, page)} className="gap-2">
                    <Search className="h-4 w-4" />
                    Apply Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                {templateView === 'global' && 'Global Templates'}
                {templateView === 'org' && 'Organization Templates'}
                {templateView === 'all' && 'All Reports'}
              </span>
              {get_debrief_pdf_report?.debrief_pdf_report?.count > 0 && (
                <Badge variant="outline">
                  {get_debrief_pdf_report?.debrief_pdf_report?.count} total
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : results && results.length > 0 ? (
              <div className="space-y-3">
                {results.map((result) => {
                  const selectedTemplate = templates?.find((rw) => rw.id == result?.template_id);
                  const displayTemplateName = result?.global_template_name || result?.org_template_name || result?.report_name || selectedTemplate?.name;
                  
                  return (
                    <Card key={result.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-lg">
                                  {displayTemplateName || `Report #${result.id}`}
                                </h3>
                                <Badge variant="outline" className="text-xs">
                                  #{result.id}
                                </Badge>
                                {result.global_template && (
                                  <Badge className="gap-1 bg-blue-100 text-blue-700 hover:bg-blue-200">
                                    <Globe className="h-3 w-3" />
                                    Global
                                  </Badge>
                                )}
                                {result.org_template && (
                                  <Badge className="gap-1 bg-purple-100 text-purple-700 hover:bg-purple-200">
                                    <Building2 className="h-3 w-3" />
                                    Org
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-600">
                                {selectedTemplate?.type && `${selectedTemplate.type} • `}
                                Status: {result.status_text || result.status}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  ResetUpdates();
                                  setTimeout(() => navigate(`/app/pdf-reports/edit/${result.id}`), 500);
                                }}
                                className="gap-2"
                              >
                                <Edit className="h-4 w-4" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => {
                                  ResetUpdates();
                                  setTimeout(() => navigate(`/app/pdf-reports/details/${result.id}`), 500);
                                }}
                                className="gap-2"
                              >
                                <Eye className="h-4 w-4" />
                                Details
                              </Button>
                            </div>
                          </div>

                          <Separator />

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500 font-medium mb-1">Organization</p>
                              <p className="text-slate-900">
                                #{result.organization} - {result.organization_name}
                              </p>
                            </div>
                            {result.employee && (
                              <div>
                                <p className="text-slate-500 font-medium mb-1">Employee</p>
                                <p className="text-slate-900">
                                  {result.employee_name || result.employee_email || `#${result.employee}`}
                                </p>
                              </div>
                            )}
                            {/* {result.template_id && (
                              <div>
                                <p className="text-slate-500 font-medium mb-1">Template ID</p>
                                <p className="text-slate-900">#{result.template_id}</p>
                              </div>
                            )} */}
                            {result.template_name && (
                              <div>
                                <p className="text-slate-500 font-medium mb-1">Template Name</p>
                                <p className="text-slate-900">#{result.template_name}</p>
                              </div>
                            )}
                            <div>
                              <p className="text-slate-500 font-medium mb-1">Last Updated</p>
                              <p className="text-slate-900">
                                {result.updated_at && format(new Date(result.updated_at), 'MMM dd, yyyy h:mm a')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No reports found</h3>
                <p className="text-sm text-slate-600 mb-6">
                  {activeFiltersCount > 0 
                    ? "Try adjusting your filters or create a new report" 
                    : "Get started by creating your first PDF report"}
                </p>
                <Button onClick={() => navigate('/app/pdf-reports/create')} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Report
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(null, { activePage: page - 1 })}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(null, { activePage: pageNum })}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  {totalPages > 5 && <span className="px-2">...</span>}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(null, { activePage: page + 1 })}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TemplateList;
